import React, { useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { formatDate } from '../../utils/helpers';
import { motion, AnimatePresence } from 'framer-motion';
import { useLeadIntel } from '../../context/LeadIntelContext';

const SignalFeed = () => {
  const { signals, analyzeSignal } = useLeadIntel();
  const [analyzingId, setAnalyzingId] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);

  const handleAnalyze = async (signal) => {
    if (analyzingId === signal.signal_id) {
      setAnalyzingId(null);
      setAnalysisResult(null);
      return;
    }
    
    setAnalyzingId(signal.signal_id);
    setAnalysisResult(null);
    
    const result = await analyzeSignal(signal);
    setAnalysisResult(result);
  };

  const getPriorityColor = (prio) => {
    switch(prio) {
      case 'Høy': return 'bg-rose-50 text-rose-700 border-rose-100';
      case 'Middels': return 'bg-amber-50 text-amber-700 border-amber-100';
      default: return 'bg-slate-50 text-slate-600 border-slate-100';
    }
  };

  return (
    <div className="space-y-4">
      {signals.map((signal, idx) => (
        <motion.div 
          key={signal.signal_id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: idx * 0.05 }}
          className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-primary-300 transition-all group"
        >
          <div className="flex justify-between items-start gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest border ${getPriorityColor(signal.priority_hint)}`}>
                  {signal.priority_hint}
                </span>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                  {signal.signal_type}
                </span>
                <span className="text-[10px] text-slate-300">• {formatDate(signal.occurred_at)}</span>
              </div>
              <h4 className="font-bold text-slate-900 text-sm mb-1">{signal.company_name}: {signal.title}</h4>
              <p className="text-xs text-slate-500 line-clamp-2">{signal.summary}</p>
            </div>
            
            <button 
              onClick={() => handleAnalyze(signal)}
              className={`p-2 rounded-xl transition-all flex flex-col items-center gap-1 min-w-[60px] ${analyzingId === signal.signal_id ? 'bg-primary-600 text-white' : 'bg-slate-50 text-slate-400 hover:bg-primary-50 hover:text-primary-600'}`}
            >
              <SafeIcon icon={analyzingId === signal.signal_id && !analysisResult ? "🔄" : "🤖"} className={`w-5 h-5 ${analyzingId === signal.signal_id && !analysisResult ? 'animate-spin' : ''}`} />
              <span className="text-[9px] font-black uppercase">Coach</span>
            </button>
          </div>

          <AnimatePresence>
            {analyzingId === signal.signal_id && analysisResult && (
              <motion.div 
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-4 pt-4 border-t border-slate-100 bg-slate-50/50 -mx-5 -mb-5 p-5 rounded-b-2xl">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 shrink-0">
                      <SafeIcon icon="💡" />
                    </div>
                    <div className="space-y-3 flex-1">
                      <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Anbefalt neste steg</p>
                        <p className="text-xs font-bold text-slate-700">{analysisResult.next_step}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Utkast til melding</p>
                        <div className="bg-white p-3 rounded-xl border border-slate-200 text-xs italic text-slate-600 mt-1">
                          "{analysisResult.outreach_text}"
                        </div>
                        <button 
                          onClick={() => { navigator.clipboard.writeText(analysisResult.outreach_text); alert('Tekst kopiert!'); }}
                          className="mt-2 text-[10px] font-bold text-primary-600 hover:underline flex items-center gap-1"
                        >
                          <SafeIcon icon="📋" /> Kopier tekst
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      ))}
      
      {signals.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          <SafeIcon icon="📡" className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p className="text-sm font-bold">Ingen signaler funnet</p>
        </div>
      )}
    </div>
  );
};

export default SignalFeed;